import { makeAutoObservable } from 'mobx'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import type { IResponseCnam } from 'entities/Compliance/api/types/cnamType'
import { VoiceServicesApi } from 'entities/Compliance/api/voiceServicesApi'
import { type IResponseCallingVoiceStatus } from 'entities/Compliance/api/types/voiceServicesTypes'
import { Cnam } from './OrganizationCnam/Cnam'

export class OrganizationCnam {
  private _cnamMap: Map<number, Cnam> = new Map()
  private _deleteLoading = false

  constructor() {
    makeAutoObservable(this)
  }

  get deleteLoading() {
    return this._deleteLoading
  }

  get cnamServicesStatus() {
    const statuses = this.cnam.map((item) => item.status as IResponseCallingVoiceStatus)

    if (statuses.length === 0) return null

    if (statuses.includes('failed')) {
      return 'failed'
    }

    if (statuses.some((status) => ['under_review', 'in_review', 'pending'].includes(status))) {
      return 'pending'
    }

    return 'approved'
  }

  get isRejected() {
    return this.cnamServicesStatus === 'failed'
  }

  syncData = (data: IResponseCnam[] | null) => {
    if (!data) return

    this.addItems(data)
  }

  addItems = (items: IResponseCnam[]) => {
    items.forEach((item) => {
      this.addItem(item)
    })
  }

  addItem = (item: IResponseCnam) => {
    this._cnamMap.set(item.id, new Cnam(item))
  }

  deleteItem = async (cnam: Cnam, onSuccess?: () => void) => {
    const { id, name } = cnam

    try {
      this._deleteLoading = true
      const response = await VoiceServicesApi.deleteCnamCallerId(id)

      if (response.status === 200) {
        this._cnamMap.delete(id)
        toastStore.add({
          type: 'success',
          title: `CNAM Caller ID "${name}" deleted`,
        })
        onSuccess?.()
      }
    } catch (e) {
      logger.error(e)

      toastStore.add({
        type: 'error',
        title: 'Failed to delete CNAM caller ID',
      })
    } finally {
      this._deleteLoading = false
    }
  }

  getItem = (id: number) => {
    return this._cnamMap.get(id)
  }

  get hasCnam() {
    return Boolean(this._cnamMap.size)
  }

  get cnam() {
    return Array.from(this._cnamMap.values())
  }
}
